/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { StyledProvisioningPageHeading } from '../StyledProvisioningPageHeading';
import { StyledProvisioningPageSubheading } from '../StyledProvisioningPageSubheading';
import { Stack } from '@mui/material';
import { BeamInput } from '../../common/formik/BeamInput';
import { BeamMultiSelect } from '../../common/formik/BeamMultiSelect';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Organization } from '../../../shared/entities/organization';
import ErrorMessage from '../../common/layout/ErrorMessage';
import { BeamCancelButton } from '../../common/formik/BeamCancelButton';
import { BeamSubmitButton } from '../../common/formik/BeamSubmitButton';
import { requestCustomerGroupOptions } from '../../../store/options/actions';
import { requestRoles } from '../../../store/roles/actions';
import {useHistory, useParams} from 'react-router-dom';
import {ajaxRaw} from '../../../utils/StoreUtils';
import {AjaxError, AjaxResponse} from 'rxjs/ajax';
import {TerminalAssignmentsField} from './TerminalAssignmentsField';
import { selectCustomerGroupTypeOptions, selectRoles, selectRolesRequestError } from '../../../store';
import * as yup from 'yup';

import RoleHelpText from '../RoleHelpText';

const terminalNameProp = 'displayName';
const terminalRestrictedProp = 'restrictedDefault';

export interface TerminalField {
  displayName: string;
  customerGroup: string;
  restrictedDefault: boolean;
}

export const OrganizationForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  let { uuid } = useParams();

  const customerGroups = useSelector(selectCustomerGroupTypeOptions);
  const rolesList = useSelector(selectRoles, shallowEqual);
  const rolesRequestError = useSelector(selectRolesRequestError, shallowEqual);
  const [terminals, setTerminals] = useState<TerminalField[]>([]);

  const [submissionError, setError] = useState<string | undefined>(undefined);
  const [initialFormState, setInitialFormState] = useState<Partial<Organization>>({
    name: '',
    customerGroups: [],
    roles: [],
    terminalRestrictedMap: {},
    terminalAssignments: [],
    usersInOrg: [],
  });
  const [organization, setOrganization] = useState<Partial<Organization>>();

  useEffect(() => {
    // Request options on initial load
    dispatch(requestCustomerGroupOptions());
    dispatch(requestRoles());
  }, [dispatch]);

  const getAllProvisionedTerminals = () => {
    ajaxRaw.get(`/provisioning/terminals`).subscribe({
      next: value => {
        setTerminals(
          value.response.map(
            t =>
              ({
                displayName: t.displayName,
                customerGroup: t.customerGroup,
                restrictedDefault: t.restrictedDefault,
              } as TerminalField),
          ),
        );
      },
      error: err => {
        setError(err.message);
      },
    });
  };

  // request terminals on initial load
  useEffect(getAllProvisionedTerminals, []);

  useEffect(() => {
    if (organization) {
      const terminalsInOrg: Record<string, boolean> = organization.terminalRestrictedMap ?? {};

      // convert terminal data type from record to list to satisfy mui autocomplete
      const terminalsList = terminals
        .filter(terminal => terminalsInOrg.hasOwnProperty(terminal.displayName))
        .map(terminal => ({
          ...terminal,
          restrictedDefault: terminalsInOrg[terminal.displayName],
        }));

      setInitialFormState({
        ...organization,
        terminalRestrictedMap: {}, // set to empty
        terminalAssignments: terminalsList, // display terminal data in list format
      } as Partial<Organization>);
    }
  }, [organization, terminals]);

  const isEditMode = uuid !== undefined;
  const action = isEditMode ? 'Edit' : 'Add';

  useEffect(() => {
    // Request existing data if we're editing an entity
    if (isEditMode) {
      ajaxRaw.get(`/provisioning/organizations/${uuid}`).subscribe({
        next: value => setOrganization(value.response),
        error: err => setError(err.message),
      });
    }
  }, [isEditMode, uuid]);

  // Update error message if an error occurs fetching roles
  useEffect(() => {
    if (rolesRequestError) {
      setError(rolesRequestError);
    }
  }, [rolesRequestError]);

  const setTerminalRestrictedMapField = (terminalAssignmentsInput: any[] | undefined) => {
    let selectedTerminals: Record<string, boolean> = {};

    // manipulate user input for terminals back to record type before submitting
    if (terminalAssignmentsInput !== undefined) {
      terminalAssignmentsInput.forEach(obj => {
        if (obj.hasOwnProperty(terminalNameProp) && obj.hasOwnProperty(terminalRestrictedProp)) {
          selectedTerminals[obj[terminalNameProp]] = obj[terminalRestrictedProp];
        }
      });
    }
    return selectedTerminals;
  };

  return (
    <>
      <StyledProvisioningPageHeading>
        {action} {!isEditMode && 'new '}organization
      </StyledProvisioningPageHeading>
      <StyledProvisioningPageSubheading>
        To {action.toLowerCase()} a {!isEditMode && 'new '}organization, please complete the form below.
      </StyledProvisioningPageSubheading>
      <Formik
        enableReinitialize={true}
        initialValues={initialFormState}
        onSubmit={(values, { setSubmitting }: FormikHelpers<Partial<Organization>>) => {
          setError(undefined);
          setSubmitting(true);

          // set terminalRestrictedMap field by manipulating terminalAssignments input
          const modifiedValues = {
            ...values,
            terminalRestrictedMap: setTerminalRestrictedMapField(values.terminalAssignments),
          };

          // remove terminalAssignments field before submitting
          const { terminalAssignments, ...remainingValues } = modifiedValues;

          let url = `/provisioning/organizations`;
          let operation = ajaxRaw.postJson;
          if (isEditMode) {
            url = `/provisioning/organizations/${values.id}`;
            operation = ajaxRaw.put;
          }
          operation(url, JSON.stringify(remainingValues)).subscribe({
            next: (response: AjaxResponse<any>) => {
              setSubmitting(false);
              if (response.status === 200 || response.status === 201) {
                history.goBack();
              } else {
                setError(JSON.stringify(response.response));
              }
            },
            error: (err: AjaxError) => {
              setError(err.response.message ? err.response.message : err.message);
              setSubmitting(false);
            },
          });
        }}
        validateOnChange={true}
        validationSchema={yup.object().shape({
          name: yup
            .string()
            .required('Please enter a name for the organization.')
            .max(255, 'Organization name must be less than 255 characters.'),
          customerGroups: yup.array().required('Please select at least one customer group for the organization.'),
          roles: yup.array(),
          terminalAssignments: yup.array(),
        })}
      >
        {({ values }) => (
          <Form style={{ width: '600px', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {submissionError ? <ErrorMessage>{submissionError}</ErrorMessage> : null}

            <BeamInput name={'name'} label={'Organization Name'} required={true}></BeamInput>

            <BeamMultiSelect
              name={'customerGroups'}
              label={'Customer Groups'}
              options={customerGroups ?? []}
              placeholder={'Select customer groups'}
              getOptionLabel={option => option.toUpperCase()}
              required={true}
            />

            <TerminalAssignmentsField
              name={'terminalAssignments'}
              label={'Terminal Assignments'}
              placeholder={'Select terminals'}
              terminals={terminals ?? []}
              currentValue={values.customerGroups ?? []}
            />

            <BeamMultiSelect
              name={'roles'}
              label={'Roles'}
              options={rolesList ? rolesList.map(role => role.name) : []}
              placeholder={'Select roles'}
              helpText={
                <>
                  These roles are assigned to all users in this organization. <RoleHelpText />
                </>
              }
              getOptionLabel={option => option}
            />

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <BeamCancelButton />
              <BeamSubmitButton />
            </Stack>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default OrganizationForm;
